import type { JobsApi } from '@/features/jobs/api/job-api'
import { Id } from '@/infra/id'
import { ListJobQuestionsRequest } from '@taxfyle/api-internal/internal/job_pb'
import type { AnswersMap, Question } from '@/features/jobs/questions/questions'
import {
  mapQuestionProtoToQuestion,
  populateAnswerMapProto,
} from '@/features/jobs/questions/question-mappers'
import {
  BoolInputValue,
  InputValue,
} from '@taxfyle/api-internal/internal/question_input_value_pb'
import { isNotNil } from '@/utils/object-util'
import { retryOnRpcError } from '@/utils/grpc-util'

/**
 * Provides methods for querying for the job questionnaire.
 */
export class QuestionStore {
  constructor(private readonly _api: JobsApi) {
    this.fetchQuestions = this.fetchQuestions.bind(this)
  }

  /**
   * Fetches the questions for a job.
   *
   * @param query
   */
  async fetchQuestions(
    query: FetchQuestionsQuery,
  ): Promise<FetchQuestionsResult> {
    query.signal?.throwIfAborted()

    const request = new ListJobQuestionsRequest()
    request.setId(Id.assert(query.jobId))

    if (query.answers) {
      populateAnswerMapProto(query.answers, request.getAnswersMap())

      // If no answers were present, add a placeholder so the backend doesn't
      // default to the job answers.
      //
      // We do this to mitigate an issue where, say the first question is
      // a multi-select, if we select an option, it gets saved, and then we
      // deselect the option, we'd be sending an empty map of answers
      // in that call. The backend will then default to the job's answers,
      // which would be the one we saved earlier, so the UI will flick back to
      // the state where it was selected. If the user reloads, the selection
      // would be gone.
      if (query.answers.size === 0) {
        request
          .getAnswersMap()
          .set(
            `ARPHYX_PLACEHOLDER_${new Date().valueOf()}`,
            new InputValue().setBoolValue(new BoolInputValue().setValue(true)),
          )
      }
    }

    const response = await retryOnRpcError(
      () => this._api.jobs.listJobQuestions(request),
      { signal: query.signal },
    )

    return {
      intakeCompleted: response.getIntakeCompleted(),
      questions: response
        .getQuestionsList()
        .map(mapQuestionProtoToQuestion)
        .filter(isNotNil),
    }
  }
}

/** @see {QuestionStore.fetchQuestions} */
export interface FetchQuestionsQuery {
  jobId: Id
  answers?: AnswersMap | null
  signal?: AbortSignal
}

/** @see {QuestionStore.fetchQuestions} */
export interface FetchQuestionsResult {
  intakeCompleted: boolean
  questions: Question[]
}
